import { useEffect, useState } from "react";
import { Tabs, Tab, Box, useTheme } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box p={2}>{children}</Box>}
    </div>
  );
};

interface TabItem {
  label: string;
  component: React.ReactNode;
  icon: React.ReactNode;
}

interface ModularTabsProps {
  tabs: TabItem[];
}

const ModularTabs: React.FC<ModularTabsProps> = ({ tabs }) => {
  const [tabIndex, setTabIndex] = useState(0);
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  useEffect(() => {
    const tab_index = searchParams.get("tab_index") || "0";
    setTabIndex(Number(tab_index));
  }, []);

  const handleTabChange = (_event: React.SyntheticEvent, newIndex: number) => {
    setTabIndex(newIndex);
    navigate({ search: `?tab_index=${newIndex}` }, { replace: true });
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <Box
        sx={{
          background:
            theme.palette.mode === "light"
              ? theme.palette.customColors.white
              : theme.palette.customColors.blackBorder,
          borderRadius: "4px",
          padding: "4px",
        }}
      >
        <Tabs
          value={tabIndex}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            "& .MuiTabs-indicator": {
              backgroundColor: theme.palette.customColors.purple,
            },
            "& .MuiTabs-flexContainer": {
              display: "flex",
              justifyContent: "",
              pb: 1,
            },
            "& .MuiTabs-scrollButtons": {
              display: "flex",
              borderRadius: "50%",
              marginLeft: 1,
              marginRight: 1,
              marginTop: "0.9rem",
              border: `1px solid ${theme.palette.customColors.blackBorder}`,
              width: "25px",
              height: "25px",
              "& .MuiSvgIcon-root": {
                color: theme.palette.customColors.blackBorder,
              },
            },
            "& .MuiTabs-scrollButtons.Mui-disabled": {
              opacity: 0.2,
            },
          }}
        >
          {tabs.map((tab, index) => (
            <Tab
              key={index}
              label={
                <Box
                  display="flex"
                  alignItems="center"
                  gap={1}
                  sx={{
                    fontSize: "14px",
                    fontWeight: 700,
                    color:
                      tabIndex === index
                        ? theme.palette.customColors.purple
                        : theme.palette.customColors.greyTextColor,
                    textTransform: "none",
                    "&:hover": { color: theme.palette.customColors.purple },
                  }}
                >
                  {tab.icon} {tab.label}
                </Box>
              }
              sx={{ width: "auto", minWidth: "180px" }}
            />
          ))}
        </Tabs>
      </Box>

      <Box
        sx={{
          background:
            theme.palette.mode === "light"
              ? theme.palette.customColors.white
              : theme.palette.customColors.darkBlackBorder,
          borderRadius: "4px",
          padding: "4px",
        }}
      >
        {tabs.map((tab, index) => (
          <TabPanel key={index} value={tabIndex} index={index}>
            {tab.component}
          </TabPanel>
        ))}
      </Box>
    </Box>
  );
};

export default ModularTabs;
